import type { ApiErrorResponse } from "@chaochud/contracts";

/**
 * Foundation-only fetch client.
 *
 * Business-scoped clients (public/account/seller/admin, see
 * docs/project-structure.md section 16) are added starting in later phases.
 */
export interface ApiClientConfig {
  baseUrl: string;
  /** Optional fetch implementation override, primarily for testing. */
  fetchImpl?: typeof fetch;
  /** Called before each request; useful for attaching auth headers later. */
  getHeaders?: () => Record<string, string> | Promise<Record<string, string>>;
  /**
   * Called once when a request comes back `401`. The access token's own
   * lifetime is intentionally short (`JWT_ACCESS_TTL_SECONDS`, currently 1
   * hour) — the caller's real session length is the refresh token's TTL
   * (`JWT_REFRESH_TTL_SECONDS`, 30 days). Without this hook, every request
   * made after the access token's first hour would 401 even though the
   * refresh token is still good for weeks — indistinguishable from "the
   * user got logged out". See `apps/web/src/lib/api-clients.ts` for the
   * refresh-and-retry implementation.
   *
   * Should attempt to refresh the session and resolve with headers to
   * retry the original request with once, or `null` if no refresh was
   * possible (e.g. the refresh token has also expired) — in which case the
   * original `401` `ApiClientError` is thrown as before.
   */
  onUnauthorized?: () => Promise<Record<string, string> | null>;
}

export class ApiClientError extends Error {
  readonly statusCode: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(response: ApiErrorResponse) {
    super(response.message);
    this.name = "ApiClientError";
    this.statusCode = response.statusCode;
    this.code = response.code;
    this.details = response.details;
  }
}

export interface ApiClient {
  request<TResponse>(path: string, init?: RequestInit): Promise<TResponse>;
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const fetchImpl = config.fetchImpl ?? fetch;

  const doFetch = async (path: string, init: RequestInit | undefined, extraHeaders?: Record<string, string>) => {
    const headers = {
      "Content-Type": "application/json",
      ...(await config.getHeaders?.()),
      ...extraHeaders,
      ...init?.headers,
    };

    return fetchImpl(`${config.baseUrl}${path}`, { ...init, headers });
  };

  return {
    async request<TResponse>(path: string, init?: RequestInit): Promise<TResponse> {
      let response = await doFetch(path, init);

      if (response.status === 401 && config.onUnauthorized) {
        const refreshedHeaders = await config.onUnauthorized();
        if (refreshedHeaders) {
          response = await doFetch(path, init, refreshedHeaders);
        }
      }

      if (!response.ok) {
        const body = (await response.json().catch(() => null)) as ApiErrorResponse | null;

        if (body) {
          throw new ApiClientError(body);
        }

        throw new Error(`Request to ${path} failed with status ${response.status}`);
      }

      return (await response.json()) as TResponse;
    },
  };
}
